"use client";

import { useEffect } from "react";
import { usePathname, useParams } from "next/navigation";
import Intercom from "@intercom/messenger-js-sdk";
import useUserInfo from "@/hooks/useUserInfo";

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

// "/login-m" is the superSuperAdmin impersonation login - same auth surface as "/login",
// so it must skip /api/user/me too (otherwise: 401 → refresh fail → logout → redirect to /login).
// /tech-leaderboard: the Tech Performance landing page, wizard and guide, all reached before sign in.
const AUTH_PATHS = ["/login", "/login-m", "/register", "/forgot-password", "/reset-password", "/self-onboarding", "/tech-leaderboard"];

function isAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

export default function IntercomProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useParams();
  const onAuthRoute = isAuthPath(pathname);
  // Hide the default launcher on pages with companyId (sidebar has a Support button there)
  const hasCompanyId = Boolean(params?.companyId);
  // Skip /api/user/me on auth routes to avoid 401 → redirect loop; useUserInfo accepts optional { enabled }
  const { userId, userName, email, createdAt, loading } = useUserInfo(
    { enabled: !onAuthRoute } as Parameters<typeof useUserInfo>[0],
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!APP_ID) return;

    // On login/register etc. boot as visitor immediately so we never call /api/user/me (avoids 401 → redirect loop)
    if (onAuthRoute) {
      Intercom({ app_id: APP_ID });
      return;
    }

    const initIntercom = async () => {
      if (loading) return;

      if (userId && userName != null && email) {
        // Logged-in user: boot with user data; optionally add JWT for identity verification
        const basePayload: Parameters<typeof Intercom>[0] = {
          app_id: APP_ID,
          user_id: userId,
          name: userName,
          email,
          hide_default_launcher: hasCompanyId,
          ...(createdAt != null && { created_at: createdAt }),
        };

        try {
          const res = await fetch("/api/intercom/identity");
          if (res.ok) {
            const { jwt } = await res.json();
            Intercom({
              ...basePayload,
              api_base: "https://api-iam.intercom.io",
              intercom_user_jwt: jwt,
            });
          } else {
            Intercom(basePayload);
          }
        } catch {
          Intercom(basePayload);
        }
      } else {
        // Anonymous visitor (e.g. other public pages)
        Intercom({ app_id: APP_ID });
      }
    };

    initIntercom();
  }, [onAuthRoute, userId, userName, email, createdAt, loading, hasCompanyId]);

  return <>{children}</>;
}
